import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link } from "react-router-dom";
import { BACKEND_URL } from "@/constants/api";
import { BookOpen, ChevronLeft, AlertCircle, ArrowRight } from "lucide-react";

export default function SchoolClasses() {
  const { schoolId } = useParams();
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, classesRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/classes`, { headers }),
      ]);
      if (!schoolRes.ok || !classesRes.ok) throw new Error("Failed to load classes");
      setSchool(await schoolRes.json());
      setClasses(await classesRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <Link
        to={`/schools/${schoolId}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <BookOpen size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="dashboard-heading-sm">All Classes</h1>
          <p className="dashboard-subheading">
            {classes.length} class{classes.length !== 1 ? "es" : ""} in {school?.name}
          </p>
        </div>
      </div>

      {classes.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <BookOpen size={32} className="text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No classes yet</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            When teachers create classes linked to this school, they will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {classes.map((cls) => (
            <Link
              key={cls._id}
              to={`/schools/${schoolId}/classes/${cls._id}`}
              className="flex items-center gap-4 bg-card border border-border rounded-xl px-4 py-3 hover:border-border/80 hover:bg-muted/30 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <BookOpen size={15} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">
                  {cls.name ||
                    [cls.grade && `Grade ${cls.grade}`, cls.schoolYear]
                      .filter(Boolean)
                      .join(" · ")}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {[cls.grade && `Grade ${cls.grade}`, cls.schoolYear, cls.ownerId?.name]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {cls.reportCount !== undefined && (
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                    {cls.reportCount} report{cls.reportCount !== 1 ? "s" : ""}
                  </span>
                )}
                <ArrowRight
                  size={14}
                  className="text-muted-foreground group-hover:text-foreground transition-colors"
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
